import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "../i18n";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuthLanguageDropdown from "../Components/AuthLanguageDropdown";
import AuthLeftColumn from "../Components/AuthLeftColumn";
import { verifyVendorSignupOtpAction } from "../actions/auth";

const readPendingSignup = () => {
  try {
    return JSON.parse(sessionStorage.getItem("vendorSignupPending") || "null");
  } catch {
    return null;
  }
};

const VerifyOtp = () => {
  const { t, text } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const signupLoading = useSelector((state) => Boolean(state.auth?.vendorSignupLoading));

  const [otp, setOtp] = useState(() => Array(6).fill(""));
  const otpInputRefs = useRef([]);
  const [error, setError] = useState("");
  const [email, setEmail] = useState(() => readPendingSignup()?.email || "");
  const [phone, setPhone] = useState("");
  const [flow, setFlow] = useState("forgot-password");
  const isSignupFlow = flow === "vendor-signup" || Boolean(readPendingSignup());
  const isOtpComplete = otp.every((digit) => /^\d$/.test(digit));

  useEffect(() => {
    const pendingSignup = readPendingSignup();
    if (pendingSignup) {
      setFlow("vendor-signup");
      setEmail(pendingSignup.email || "");
      setPhone(pendingSignup.phone || "");
      return;
    }

    const storedUser = sessionStorage.getItem("forgotPasswordUser");
    if (!storedUser) {
      navigate("/forgot-password");
      return;
    }

    try {
      const user = JSON.parse(storedUser);
      setPhone(user.phone || "");
    } catch {
      sessionStorage.removeItem("forgotPasswordUser");
      navigate("/forgot-password");
    }
  }, [navigate]);

  const updateOtpFromText = (rawValue) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 6);
    const nextOtp = Array(6).fill("");
    digits.split("").forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    setError("");
    otpInputRefs.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleOtpChange = (event, index) => {
    const digits = event.target.value.replace(/\D/g, "");
    if (digits.length > 1) {
      updateOtpFromText(digits);
      return;
    }

    const nextOtp = [...otp];
    nextOtp[index] = digits.slice(-1);
    setOtp(nextOtp);
    setError("");
    if (digits && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedDigits) return;
    event.preventDefault();
    updateOtpFromText(pastedDigits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpValue = otp.join("");
    if (otp.some((digit) => !digit)) {
      setError(t("validation.otpRequired"));
      return;
    }

    if (isSignupFlow) {
      const pendingSignup = readPendingSignup();
      if (!pendingSignup?.email) {
        setError("Your signup details were not found. Please return to the signup page.");
        return;
      }

      const result = await dispatch(
        verifyVendorSignupOtpAction({
          email: pendingSignup.email,
          otp: otpValue,
        }),
      );
      if (!result?.success) {
        setError(result?.error || t("validation.otpInvalid"));
        return;
      }

      sessionStorage.removeItem("vendorSignupPending");
      navigate("/", { replace: true });
      return;
    }

    const generatedOtp = sessionStorage.getItem("forgotPasswordOtp");
    if (otpValue !== generatedOtp) {
      setError(t("validation.otpInvalid"));
      return;
    }
    sessionStorage.removeItem("forgotPasswordOtp");
    sessionStorage.removeItem("forgotPasswordUser");
    navigate("/");
  };

  const handleResendOtp = () => {
    if (isSignupFlow) return;
    sessionStorage.setItem("forgotPasswordOtp", "123456");

    setOtp(Array(6).fill(""));
    setError("");
  };

  return (
    <main className="admin-login-page admin-login-page--reference admin-login-page--otp admin-login-page--auth-shared">
      <div className="container-fluid p-0">
        <div className="row g-0 admin-login-card">
          <AuthLeftColumn />
          <div className="col-lg-6 col-md-6 col-12 admin-login-right">
            <div className="admin-login-right-inner">
              <div className="admin-login-mobile-logo d-md-none">
                <img src="/assets/lualilogoblack.svg" alt={t("ui.luali.commodities")} />
                <AuthLanguageDropdown />
              </div>

              <div className="admin-login-form-content">
                <div className="admin-login-heading">
                  <h1>{t("ui.verify.otp")}</h1>

                  <p>{t("ui.enter.the.6.digit.otp.sent.to")}{" "}
                    <strong>{isSignupFlow ? email : phone || t("ui.yourPhoneNumber")}</strong>.
                  </p>
                </div>

                <form className="admin-login-form" onSubmit={handleSubmit}>
                  <div className="admin-login-field">
                    <label htmlFor="verify-otp" className="form-label">{t("ui.otp.code")}</label>

                    <div
                      className="verify-otp-boxes"
                      role="group"
                      aria-label={t("ui.enter.6.digit.otp")}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(element) => {
                            otpInputRefs.current[index] = element;
                          }}
                          id={index === 0 ? "verify-otp" : `verify-otp-${index + 1}`}
                          className={"verify-otp-digit" + (digit ? " is-filled" : "") + (error ? " is-invalid" : "")}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          name={index === 0 ? "otp" : undefined}
                          aria-label={`${t("ui.otp.code")} ${index + 1}`}
                          aria-invalid={Boolean(error)}
                          aria-describedby={error ? "verify-otp-error" : undefined}
                          autoComplete={index === 0 ? "one-time-code" : "off"}
                          maxLength={index === 0 ? 6 : 1}
                          value={digit}
                          onChange={(event) => handleOtpChange(event, index)}
                          onFocus={(event) => event.target.select()}
                          onKeyDown={(event) => handleOtpKeyDown(event, index)}
                          onPaste={handleOtpPaste}
                          required={index === 0}
                        />
                      ))}
                    </div>
                  </div>
                  {error && <div id="verify-otp-error" className="admin-login-error">{text(error)}</div>}

                  <button type="submit" className="recent-view-all-btn w-100" disabled={!isOtpComplete || (isSignupFlow && signupLoading)}>{isSignupFlow && signupLoading ? text("Verifying...") : t("ui.verify.otp.1naztpg")}</button>
                </form>

                <div className="admin-login-bottom-text">
                  <span>{t("ui.didn.t.receive.the.otp")}</span>

                  <button type="button" onClick={handleResendOtp} disabled={isSignupFlow} title={isSignupFlow ? "No signup resend endpoint is configured." : undefined}>{t("ui.resend.otp")}</button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default VerifyOtp;
